import { Api, columnForAggregation } from "@vzbl/integrations";
import { Table, theme, Tooltip } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { LoadingOutlined } from "@ant-design/icons";

import { asKey } from "src/utils/fields";
import {
  TableHeaderCustomComponent,
  TableHeaderProvider,
} from "pages/[orgId]/explore/lib/table/TableHeader";
import {
  SearchWindowConfig,
  SearchWindowInput,
  toSearchWindowConfig,
} from "src/search/components/inputs/SearchWindow";
import { ExploreContext } from "../useExploreContext";
import { VisualizationProps } from "./visualization-props";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import {
  CompareWithConfig,
  CompareWithControls,
  toCompareWithConfig,
} from "./CompareWithControls";
import { TableColumns } from "./TableColumns";
import { ContentViews } from "./useContentView";
import { EmptyResultExplanation } from "./EmptyResultExplanation";
import {
  FormattingConfig,
  FormattingControls,
  hasTimeUnit,
  toFormattingConfig,
} from "./FormattingControls";
import { debounce } from "lodash";
import { TableLayoutSelector } from "./UnifiedTableVisualization";
import { useMeasureTableColumns } from "./hooks/useMeasureTableColumns";
import { useTableRowInteraction } from "./hooks/useTableRowInteraction";
import { useTableSorting } from "./hooks/useTableSorting";
import { useTablePagination } from "./hooks/useTablePagination";
import { useAggregationSubscription } from "./hooks/useAggregationSubscription";
import { filterTableColumns } from "./utils/table-utils";
import { ColumnFormula } from "src/search/types";

type Config = {
  columns: string[];
  layout?: "flat" | "pivot" | "nested";
} & SearchWindowConfig &
  FormattingConfig &
  CompareWithConfig;

const toConfig: (data: Config | any) => Config = (data) => ({
  columns: Array.isArray(data?.columns) ? data.columns : [],
  layout: data?.layout || "flat",
  ...toSearchWindowConfig(data),
  ...toFormattingConfig(data),
  ...toCompareWithConfig(data),
});

function buildAllColumns(
  groupBys: any[] | undefined,
  aggregations: any[] | undefined,
  formulas: ColumnFormula[] | undefined,
  columnInfo: Api.Schema.ColumnInfo | undefined
): string[] {
  const columns: string[] = [];

  // Add group by columns
  if (groupBys?.length) {
    columns.push(...groupBys.map((g) => asKey(g)));
  }

  // Add aggregation columns
  if (aggregations?.length) {
    columns.push(...aggregations.map((agg) => columnForAggregation(agg)));
  } else if (columnInfo?.length) {
    const availableAggregations = columnInfo.filter((c) => c.reducer);
    if (availableAggregations.length > 0) {
      columns.push(...availableAggregations.map((agg) => agg.key));
    }
  }

  // Add formula columns
  if (formulas?.length) {
    columns.push(...formulas.map((formula) => formula.name));
  }

  return columns;
}

export function MeasureTable(props: VisualizationProps) {
  const { token } = theme.useToken();
  const { aggregations, view, params } = props.context;
  const config = useMemo(() => toConfig(view.config), [view.config]);
  const columnInfo = useMemo(
    () => aggregations.data?.meta?.columnInfo || [],
    [aggregations.data?.meta?.columnInfo]
  );
  const { onRow } = useTableRowInteraction({ context: props.context });

  const { columns } = useMeasureTableColumns({
    context: props.context,
    config,
    token,
  });

  const { columnOrderSorter } = useTableSorting({
    columns: config.columns,
    config,
  });

  const tableColumns = useMemo(() => {
    const filteredColumns = filterTableColumns(columns, config.columns);
    return filteredColumns.sort(columnOrderSorter);
  }, [columns, config.columns, columnOrderSorter]);

  const explanation = aggregations.data?.meta?.explanation || [];

  // Handle search window updates
  useEffect(() => {
    if (config.sortBy && config.sortDirection) {
      aggregations.setWindow({
        limit: config.limit,
        order: [
          {
            by: config.sortBy,
            direction: config.sortDirection,
          },
        ],
      });
    }
  }, [config.sortBy, config.sortDirection, config.limit]);

  // Use the new subscription hook
  useAggregationSubscription(aggregations);

  const data = aggregations.data?.data || [];

  const { pagination } = useTablePagination({
    dataLength: data.length,
    height: 0, // Will be set by DynamicCanvas
  });

  // Get all available columns
  const allColumns = useMemo(() => {
    return buildAllColumns(
      params.state.groupBys,
      params.state.aggregations,
      params.state.formulas,
      columnInfo
    );
  }, [
    params.state.groupBys,
    params.state.aggregations,
    params.state.formulas,
    columnInfo,
  ]);

  // Set default columns if none are selected or if current columns don't match available columns
  useEffect(() => {
    const configColumns = config.columns || [];
    // Only update if we have columns to show and either:
    // 1. No columns are currently selected, or
    // 2. Current columns don't match any available columns
    if (
      allColumns.length > 0 &&
      (configColumns.length === 0 ||
        (columnInfo.length > 0 &&
          !columnInfo.some((c) => configColumns.includes(c.key))))
    ) {
      view.setConfig({
        ...config,
        columns: allColumns,
      });
    }
  }, [config, allColumns, view, columnInfo]);

  // Memoize the table content to prevent unnecessary re-renders
  const tableContent = useMemo(() => {
    return (
      <TableHeaderProvider>
        <div className={`compact-table clickable-rows`}>
          <Table
            dataSource={data}
            showHeader={(aggregations.data?.data?.length || 0) > 0}
            components={{ header: { cell: TableHeaderCustomComponent } }}
            showSorterTooltip={false}
            size="small"
            locale={{
              emptyText:
                data.length === 0 ? (
                  <EmptyResultExplanation
                    explanation={explanation}
                    searchContext={props.context}
                  />
                ) : null,
            }}
            onChange={(pagination, filters, sorter) => {
              if (Array.isArray(sorter)) {
                return;
              }
              if (!sorter.column || !sorter.order) {
                // Clear sorting
                aggregations.setWindow({
                  limit: config.limit,
                  order: [],
                });
                view.setConfig({
                  ...config,
                  sortBy: undefined,
                  sortDirection: undefined,
                });
                return;
              }
              const newSortDirection =
                sorter.order === "ascend" ? "ascending" : "descending";
              // Update window state immediately
              aggregations.setWindow({
                limit: config.limit,
                order: [
                  {
                    by: sorter.field as string,
                    direction: newSortDirection,
                  },
                ],
              });
              // Then update the view config
              view.setConfig({
                ...config,
                sortBy: sorter.field as string,
                sortDirection: newSortDirection,
              });
            }}
            onRow={onRow}
            loading={{
              spinning: aggregations.isLoading,
              indicator: <LoadingOutlined />,
            }}
            rowKey={(r) => JSON.stringify(r)}
            columns={tableColumns}
            pagination={pagination}
            scroll={{ x: "100%" }}
          />
        </div>
      </TableHeaderProvider>
    );
  }, [
    data,
    aggregations,
    config,
    onRow,
    tableColumns,
    pagination,
    explanation,
  ]);

  return (
    <DynamicCanvas>
      {({ height, width }) => {
        // Update pagination with actual height
        pagination.pageSize = Math.floor((height - (30 + 34)) / 38);

        const skip = (pagination.current! - 1) * pagination.pageSize;
        const currentItems = data.slice(skip).slice(0, pagination.pageSize);

        return <div style={{ width }}>{tableContent}</div>;
      }}
    </DynamicCanvas>
  );
}

export function MeasureTableControls(props: { context: ExploreContext }) {
  const { view, params, aggregations: aggr } = props.context;
  const config = useMemo(() => toConfig(view.config), [view.config]);
  const columnInfo = useMemo(
    () => aggr.data?.meta?.columnInfo || [],
    [aggr.data?.meta?.columnInfo]
  );

  // Get all available columns including group bys and aggregations
  const allColumns = useMemo(() => {
    return buildAllColumns(
      params.state.groupBys,
      params.state.aggregations,
      params.state.formulas,
      columnInfo
    );
  }, [
    params.state.groupBys,
    params.state.aggregations,
    params.state.formulas,
    columnInfo,
  ]);

  const diffMethod = params.state.compareWith?.method || "";
  const hasCompare = diffMethod === "tuple";
  const configColumns = config.columns || [];
  const columnFormulaNames =
    params.state.formulas?.map((formula) => formula.name) || [];
  const hasTime = hasTimeUnit(props.context.aggregations);

  // Debounce column changes
  const updateColumns = useCallback(
    (newColumns: string[]) => {
      view.setConfig({ ...config, columns: newColumns });
    },
    [config, view]
  );

  const debouncedUpdateColumns = useMemo(
    () => debounce(updateColumns, 300),
    [updateColumns]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateColumns.cancel();
    };
  }, [debouncedUpdateColumns]);

  // Handle layout changes
  const handleLayoutChange = (
    layout: "flat" | "pivot" | "nested",
    contentView: ContentViews,
    newConfig: any = {}
  ) => {
    view.set(contentView);
    view.setConfig({ ...view.config, ...newConfig, layout });
  };

  return (
    <div>
      {/* Add layout selector */}
      <TableLayoutSelector
        currentLayout={config.layout || "flat"}
        onLayoutChange={handleLayoutChange}
        context={props.context}
      />
      <div style={{ marginTop: 8 }}>
        <TableColumns
          selectedColumns={configColumns}
          defaultColumns={allColumns}
          allColumns={allColumns}
          columnFormulaNames={columnFormulaNames}
          columnInfo={columnInfo}
          onSelectColumn={(column) => {
            const newColumns = [...configColumns];
            if (!newColumns.includes(column)) {
              newColumns.push(column);
              debouncedUpdateColumns(newColumns);
            }
          }}
          onRemoveColumn={(column) => {
            const newColumns = configColumns.filter((c) => c !== column);
            view.setConfig({ ...config, columns: newColumns });
          }}
          onReorderColumns={(newColumns) => {
            debouncedUpdateColumns(newColumns);
          }}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <div className="section-header">Display Options</div>
        <SearchWindowInput
          limit={config.limit}
          setLimit={(limit: number) => {
            props.context.aggregations.setWindow({
              ...props.context.aggregations.window,
              limit,
            });
            view.setConfig({ ...config, limit });
          }}
          sortBy={config.sortBy}
          setSortBy={(sortBy: "ascending" | "descending") => {
            if (config.sortBy) {
              props.context.aggregations.setWindow({
                ...props.context.aggregations.window,
                order: [
                  {
                    by: config.sortBy,
                    direction: sortBy,
                  },
                ],
              });
            }
            view.setConfig({ ...config, sortBy });
          }}
          sortDirection={config.sortDirection}
          setSortDirection={(sortDirection: "ascending" | "descending") => {
            if (config.sortBy) {
              props.context.aggregations.setWindow({
                ...props.context.aggregations.window,
                order: [
                  {
                    by: config.sortBy,
                    direction: sortDirection,
                  },
                ],
              });
            }
            view.setConfig({ ...config, sortDirection });
          }}
          columns={allColumns.map((col) => ({ label: col, value: col }))}
        />
      </div>
      {hasTime && (
        <div style={{ width: "100%" }}>
          <FormattingControls
            disabled={!hasTime}
            timeUnit={config.timeUnit}
            onChangeTimeUnit={(timeUnit) =>
              props.context.view.setConfig({ ...config, timeUnit })
            }
          />
        </div>
      )}
      {hasCompare && (
        <CompareWithControls
          disabled={!hasCompare}
          changeColor={config.changeColor}
          showChangeAs={config.showChangeAs}
          showChangeValues={config.showChangeValues}
          onChangeShowChangeAs={(showChangeAs) =>
            view.setConfig({ ...config, showChangeAs })
          }
          onChangeShowChangeValues={(showChangeValues) =>
            view.setConfig({ ...config, showChangeValues })
          }
          onChangeColor={(changeColor) =>
            view.setConfig({ ...config, changeColor })
          }
        />
      )}
    </div>
  );
}
